import React, {useEffect, useState} from "react"
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useSolv} from "./components/SolvProvider"
import {useIntlEx} from "./components/IntlUtils"
import {Alert, Button, Div, Icon, MainContainer, OtpInput, SelectList} from "./components/FormComps"
import {PageDialog, PageDialogContainer, PageDialogMainPanel} from "./components/PageDialog"
import {GoogleButton} from "./components/GoogleButton"
import {MicrosoftButton} from "./components/MicrosoftButton";
import {LinkedInButton} from "./components/LinkedInButton"
import {isBlankString} from "./components/StringUtils";
import {validateEmailAddress} from "./components/ValidationUtils";
import {PageDialogFooter, PageDialogHeader, PageDialogSider} from "./components/PageDialogComps";
import getLogger from "./components/Logging.js"
import "./App.css"
import "./SignInSignUp.css"
import {gotoDest, peekReferrer} from "./components/SigninupUtils";

const log = getLogger("Signin")

function hashToStep(hash) {
  if (hash) {
    if (hash.startsWith("#")) {
      return hash.slice(1).toUpperCase()
    }
    else {
      return hash.toUpperCase()
    }
  }
  else {
    return null
  }
}

export default function Signin(props) {

  const {env, auth: {signin, generateMagicLink, generateOtp}, setBusy, setFatal} = useSolv()
  const {intl} = useIntlEx()

  const params = useParams()
  const location = useLocation()
  const history = useHistory()

  const queryParams = new URLSearchParams(location.search)

  const [emailAddress, setEmailAddress] = useState(queryParams.get("emailAddress"))
  const [magicCode, setMagicCode] = useState(params.magicCode)
  const [authenticatedPayload, setAuthenticatedPayload] = useState(null)
  const [aliasedAccounts, setAliasedAccounts] = useState(null)
  const [aliasedAccount, setAliasedAccount] = useState(null)
  const [step, setStep] = useState(null)
  const [generateVerifyEmailClickCount, setGenerateVerifyEmailClickCount] = useState(0)

  const [alert, setAlert] = useState(null)

  useEffect(() => {
    if (magicCode) {
      signinWithMagicCode()
    }
  },[magicCode])

  useEffect(() => {
    setStep(hashToStep(location.hash))
  }, [location])

  async function handleGoogleLoginSuccess(response){
    try {
      // log.debug("googleHandleSuccessfulLogin: response=", response)
      setBusy(intl.msg("working"))
      const payload = {
        idProvider: "GOOGLE",
        profileObj: response.profileObj,
        authToken: response.tokenId
      }
      setAuthenticatedPayload(payload)
      await doSignin(payload)
    }
    catch (err) {
      handleError(err)
    }
    finally {
      setBusy(null)
    }
  }

  function handleGoogleLoginError(error) {
    setAlert({type: "error", text: intl.msg("error_failed_google_auth")})
  }

  async function handleAliasedAccountChange(v) {
    setAliasedAccount(v)
    let payload = authenticatedPayload
    payload.emailAddress = v.value
    setBusy(intl.msg("working"))
    try {
      await doSignin(payload)
    }
    finally {
      setBusy(null)
    }
  }

  async function handleMicrosoftLoginSuccess(data){
    try {
      setBusy(intl.msg("working"))
      const payload = {
        idProvider: "MICROSOFT",
        account: data.account,
        authToken: data
      }
      setAuthenticatedPayload(payload)
      await doSignin(payload)
    }
    catch (err) {
      handleError(err)
    }
    finally {
      setBusy(null)
    }
  }

  function handleMicrosoftLoginError(err) {
    log.error("handleMicrosoftLoginError: ERROR=", err)
    setAlert({type: "error", text: intl.msg("error_failed_microsoft_auth")})
  }

  async function handleLinkedInLoginSuccess(response){
    try {
      setBusy(intl.msg("working"))
      const payload = {
        idProvider: "LINKEDIN",
        code: response
      }
      setAuthenticatedPayload(payload)
      await doSignin(payload)
    }
    catch (err) {
      handleError(err)
    }
    finally {
      setBusy(null)
    }
  }

  function handleLinkedInLoginError(err){
    log.error("handleLinkedInLoginError: ERROR=", err)
    setAlert({type: "error", text: intl.msg("error_failed_linkedin_auth")})
  }

  function handleEmailAddressChange(e) {
    e.preventDefault()
    setEmailAddress(e.target.value)
  }

  async function handleEmailContinueClick() {
    try {
      setBusy(intl.msg("working"))
      if (isBlankString(emailAddress) || !validateEmailAddress(emailAddress)) {
        setAlert({type: "error", text: intl.msg("error_invalid_input_email_address")})
      }
      else {
        const payload = {
          op: "SIGN_IN",
          emailAddress: emailAddress,
          dest: queryParams.get("dest"),
        }
        await generateMagicLink(payload)
        setAlert(null)
        queryParams.set("emailAddress", emailAddress)
        window.location.hash = "#check_email"
        window.location.search = queryParams.toString()
      }
    }
    catch (err) {
      handleError(err)
    }
    finally {
      setBusy(null)
    }
  }

  async function handleOtpChange(v) {
    const payload = {
      op: "SIGN_UP",
      idProvider: "OTP",
      otp: v,
    }
    await doSignin(payload)
  }

  async function generateVerifyEmail() {

    if (isBlankString(emailAddress)) {
      setAlert({type: "error", text: "Please enter email address."})
      return
    }

    let params = {
      idProvider: "OTP",
      dest: peekReferrer()
    }

    let payload = {
      op: "SIGN_IN",
      emailAddress: emailAddress,
      params: params
    }

    setBusy(intl.msg("working"))

    try {

      await generateOtp(payload)

      setAlert(null)
      setGenerateVerifyEmailClickCount(prev => (prev || 0) + 1)
      setStep("CHECK_OTP")

    }
    catch (error) {
      handleError(error)
    }
    finally {
      setBusy(null)
    }

  }


  async function signinWithMagicCode() {
    try {
      setBusy(intl.msg("working"))
      const payload = {
        op: "SIGN_IN",
        idProvider: "MAGICLINK",
        code: magicCode
      }
      setAlert(null)
      await doSignin(payload)
    }
    catch (err) {
      handleError(err)
    }
    finally {
      setBusy(null)
    }
  }

  async function doSignin(payload){
    try {
      const data = await signin(payload)
      gotoDest(props, data)
    }
    catch (err) {
      log.error(err)
      handleError(err)
    }
  }

  function handleError(error) {
    if (error && error.code) {
      // log.debug("handleError", error.code)
      const code = error.code
      switch (code) {
        case "MULTIPLE_ALIASED_ACCOUNTS":
          if (error.details?.accounts) {
            const opts = error.details.accounts.map(a => {
              return ({
                label: `${a.tenantName} (${a.emailAddress })`,
                value: a.emailAddress,
              })
            })
            opts.sort((a, b) => a.label.localeCompare(b.label));
            setAliasedAccounts(opts)
            setAliasedAccount(null)
            setAlert(null)
            setStep("SELECT_ALIASED_ACCOUNT")
          }
          else {
            setAlert({type: "error", text: intl.msg("error_failed")})
          }
          break
        case "UNAUTHORIZED":
        case "NOT_FOUND":
          setAlert({type: "error", text: intl.msg("error_unknown_user")})
          break
        case "BANNED_USER":
        case "BANNED_TENANT":
          setAlert({type: "error", text: intl.msg("error_banned_user")})
          break
        case "BANNED_EMAIL_ADDRESS":
          setAlert({type: "error", text: intl.msg("error_banned_email_address")})
          break
        case "INVALID_EMAIL_ADDRESS":
          setAlert({type: "error", text: intl.msg("error_invalid_email_address")})
          break
        case "INVALID_RECAPTCHA":
          setAlert({type: "error", text: intl.msg("error_invalid_recaptcha")})
          break
        case "INVALID_MAGIC_LINK":
          setAlert({type: "error", text: intl.msg("error_invalid_magic_link")})
          break
        case "DUPLICATE_MAGIC_LINK":
          setAlert({type: "error", text: intl.msg("error_duplicate_magic_link")})
          break
        case "INVITING":
          setAlert({type: "error", text: intl.msg("signin_error_inviting", {a: chunks => <a className="text-link" href={`/signup/${error.details.userId}`}>{chunks}</a>})})
          break
        case "UNAVAILABLE":
          window.location = "/unavailable"
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed")})
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  return (
    <>
      <MainContainer>
        <PageDialogContainer>
          {
            ["EMAIL"].includes(step) ? (

              <PageDialog header={PageDialogHeader} sider={PageDialogSider} footer={PageDialogFooter} size="md" align="center">
                <PageDialogMainPanel title={intl.msg("signin_with_email_title")} alert={alert}>
                  <p className="mt-3">
                    {intl.msg("signin_with_email_text", {a_recaptcha: chunks => <a className="def-link" href="https://en.wikipedia.org/wiki/ReCAPTCHA" target="_blank">{chunks}</a>})}
                  </p>
                  <input className="form-control" type="text" key="inp_emailAddress" id="inp_emailAddress" style={{width: "300px", padding: "20px"}} placeHolder={intl.msg("your_email_address_placeholder")} value={emailAddress} onChange={handleEmailAddressChange}/>
                  <button className="btn btn-primary mt-3" style={{minWidth: "100px", padding: "8px", marginTop: "10px"}} onClick={handleEmailContinueClick}>
                    {intl.msg("continue")}
                  </button>
                  <div className="mt-3">
                    <a className="text-link" href="/signin">{intl.msg("signin_with_email_use_another_means")}</a>
                  </div>
                </PageDialogMainPanel>
              </PageDialog>

            ) : ["CHECK_OTP"].includes(step) ? (

              <PageDialog header={PageDialogHeader} sider={PageDialogSider} footer={PageDialogFooter} size="md" align="center">
                <PageDialogMainPanel title={intl.msg("check_otp_title")} alert={alert}>
                  <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                    <p className="mt-1">
                      {intl.msg("check_otp_text_1", {brand: env.displayName, emailAddress: emailAddress})}
                    </p>

                    <div className="mt-2" style={{width: "440px"}}>
                      <OtpInput length={8} onChange={handleOtpChange}/>
                    </div>

                    <p className="mt-3">
                      {intl.msg("check_otp_text_2", {brand: env.displayName, emailAddress: emailAddress})}
                      {
                        generateVerifyEmailClickCount <= 1 ? (
                          intl.msg("check_otp_text_3", {brand: env.displayName, emailAddress: emailAddress, a_regenOtp: (chunks) => <a className="text-link" href="#" onClick={generateVerifyEmail}>{chunks}</a>})
                        ) : (
                          <></>
                        )
                      }
                    </p>
                  </div>
                </PageDialogMainPanel>
              </PageDialog>

            ) : ["CHECK_EMAIL"].includes(step) ? (

              <PageDialog header={PageDialogHeader} sider={PageDialogSider} footer={PageDialogFooter} size="md" align="center">
                <PageDialogMainPanel title={intl.msg("check_email_title")} alert={alert}>
                  <Alert type="notice" text={intl.msg("magic_link_deprecation", {brand: env.displayName})}/>
                  <p className="mt-3">
                    {intl.msg("check_email_text", {brand: env.displayName, emailAddress: emailAddress, b: chunks => <strong>{chunks}</strong>, em: chunks => <em>{chunks}</em>, p: chunks  => <p className="mt-3">{chunks}</p>})}
                  </p>
                  <button type="button" className="btn btn-primary mt-3" onClick={handleEmailContinueClick}>{intl.msg("resend")}</button>
                </PageDialogMainPanel>
              </PageDialog>

            ) : ["SELECT_ALIASED_ACCOUNT"].includes(step) ? (

              <PageDialog header={PageDialogHeader} sider={PageDialogSider} footer={PageDialogFooter} size="md" align="center">
                <PageDialogMainPanel title={intl.msg("multiple_aliased_accounts_title", {brand: env.displayName})} alert={alert}>
                  <p className="mt-3">
                    {intl.msg("multiple_aliased_accounts_text", {brand: env.displayName, emailAddress: emailAddress})}
                  </p>
                  <SelectList
                    id="inp_aliasedAccount"
                    value={aliasedAccount}
                    options={aliasedAccounts}
                    menuIsOpen={true}
                    onChange={handleAliasedAccountChange}
                    isDisabled={false}/>
                </PageDialogMainPanel>
              </PageDialog>

            ) : (

              <PageDialog header={PageDialogHeader} sider={PageDialogSider} footer={PageDialogFooter} size="md" align="center">
                <PageDialogMainPanel title={intl.msg("signin_title", {brandName: env.displayName})} alert={alert}>
                  <p className="mt-2 mb-2">
                    {intl.msg("signin_text")}
                  </p>

                  <Div className="mt-1" hidden={!env.googleClientId}>
                    <GoogleButton
                      onSuccess={handleGoogleLoginSuccess}
                      onError={handleGoogleLoginError}
                      disabled={false}
                      buttonText={intl.msg("signin_with_google")}/>
                  </Div>

                  <Div className="mt-2" hidden={!env.linkedinClientId}>
                    <LinkedInButton
                      onSuccess={handleLinkedInLoginSuccess}
                      onError={handleLinkedInLoginError}
                      buttonText={intl.msg("signin_with_linkedin")}/>
                  </Div>

                  <Div className="mt-2" hidden={!env.microsoftClientId}>
                    <MicrosoftButton
                      onSuccess={handleMicrosoftLoginSuccess}
                      onError={handleMicrosoftLoginError}
                      buttonText={intl.msg("signin_with_microsoft")}/>
                  </Div>

                  <Div className="d-flex flex-row align-items-center mt-1 gap-1" hidden={!env.googleClientId && !env.microsoftClientId && !env.linkedinClientId}>
                    <span className="text-muted">-</span><span>or</span><span className="text-muted">-</span>
                  </Div>
                  <div className="mt-2">
                    <input className="form-control" style={{width: "210px"}} type="email" value={emailAddress} placeholder={"Enter email address"} onChange={handleEmailAddressChange}/>
                    <Button className="btn-signin btn-signin-email mt-1" type="button" hidden={!emailAddress || emailAddress.trim().length === 0} onClick={generateVerifyEmail}>
                      <div className="btn-signin-icon">
                        <Icon name="envelope2"/>
                      </div>
                      <span className="btn-signin-text">{intl.msg("signin_with_email")}</span>
                    </Button>
                  </div>
                  <div className="mt-3">
                    <p>{intl.msg("accept_terms_by_continuing")}</p>
                  </div>
                  <div className="mt-4" style={{borderTop: "1px solid #555", paddingTop: "20px"}}>{intl.msg("dont_have_an_account")}</div>
                </PageDialogMainPanel>
              </PageDialog>

            )
          }
        </PageDialogContainer>
      </MainContainer>
    </>
  )
}